import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '../client'
import type {
  WorkOrder,
  WorkOrderPageResponse,
  WorkOrderFilter,
  DispatchRequest,
  ReasonRequest,
  ActionLog,
  StartDiagnosisRequest,
  CompleteDiagnosisRequest,
  PreparePartsRequest,
  RepairStartRequest,
  RepairCompleteRequest,
  AcceptanceRequest,
  BindDeviceAssetRequest,
  PartItemRequest,
} from '../types/work-order'

const QUERY_KEY = {
  list: (filter?: WorkOrderFilter) => ['work-orders', filter] as const,
  detail: (id: number) => ['work-orders', id] as const,
  timeline: (id: number) => ['work-orders', id, 'timeline'] as const,
}

function buildFilterQuery(filter: WorkOrderFilter = {}) {
  const { page = 0, size = 20, ...rest } = filter
  const params = new URLSearchParams()
  params.set('page', String(page))
  params.set('size', String(size))
  if (rest.status) params.set('status', rest.status)
  if (rest.keyword) params.set('keyword', rest.keyword)
  if (rest.sourceType) params.set('sourceType', rest.sourceType)
  if (rest.engineerId) params.set('engineerId', rest.engineerId)
  if (rest.createdFrom) params.set('createdFrom', rest.createdFrom)
  if (rest.createdTo) params.set('createdTo', rest.createdTo)
  return params.toString()
}

function useInvalidateWorkOrderQueries() {
  const queryClient = useQueryClient()
  return (id: number) => {
    queryClient.invalidateQueries({ queryKey: QUERY_KEY.detail(id) })
    queryClient.invalidateQueries({ queryKey: QUERY_KEY.timeline(id) })
    queryClient.invalidateQueries({ queryKey: ['work-orders'] })
  }
}

export function useWorkOrders(filter: WorkOrderFilter = {}) {
  return useQuery({
    queryKey: QUERY_KEY.list(filter),
    queryFn: () => apiClient.get<WorkOrderPageResponse>(`/api/work-orders?${buildFilterQuery(filter)}`),
  })
}

export function useWorkOrderDetail(id: number) {
  return useQuery({
    queryKey: QUERY_KEY.detail(id),
    queryFn: () => apiClient.get<WorkOrder>(`/api/work-orders/${id}`),
    enabled: !!id,
  })
}

export function useWorkOrderTimeline(id: number) {
  return useQuery({
    queryKey: QUERY_KEY.timeline(id),
    queryFn: () => apiClient.get<ActionLog[]>(`/api/work-orders/${id}/timeline`),
    enabled: !!id,
  })
}

export function useAcceptWorkOrder() {
  const invalidate = useInvalidateWorkOrderQueries()
  return useMutation({
    mutationFn: (id: number) => apiClient.post<WorkOrder>(`/api/work-orders/${id}/accept`, {}),
    onSuccess: (_data, id) => invalidate(id),
  })
}

export function useRejectWorkOrder() {
  const invalidate = useInvalidateWorkOrderQueries()
  return useMutation({
    mutationFn: ({ id, reason }: { id: number; reason: string }) =>
      apiClient.post<WorkOrder>(`/api/work-orders/${id}/reject`, { reason } as ReasonRequest),
    onSuccess: (_data, { id }) => invalidate(id),
  })
}

export function useCancelWorkOrder() {
  const invalidate = useInvalidateWorkOrderQueries()
  return useMutation({
    mutationFn: ({ id, reason }: { id: number; reason: string }) =>
      apiClient.post<WorkOrder>(`/api/work-orders/${id}/cancel`, { reason } as ReasonRequest),
    onSuccess: (_data, { id }) => invalidate(id),
  })
}

export function useCloseExceptionWorkOrder() {
  const invalidate = useInvalidateWorkOrderQueries()
  return useMutation({
    mutationFn: ({ id, reason }: { id: number; reason: string }) =>
      apiClient.post<WorkOrder>(`/api/work-orders/${id}/close-exception`, {
        reason,
      } as ReasonRequest),
    onSuccess: (_data, { id }) => invalidate(id),
  })
}

export function useReopenWorkOrder() {
  const invalidate = useInvalidateWorkOrderQueries()
  return useMutation({
    mutationFn: ({ id, reason }: { id: number; reason: string }) =>
      apiClient.post<WorkOrder>(`/api/work-orders/${id}/reopen`, { reason } as ReasonRequest),
    onSuccess: (_data, { id }) => invalidate(id),
  })
}

export function useDispatchWorkOrder() {
  const invalidate = useInvalidateWorkOrderQueries()
  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: DispatchRequest }) =>
      apiClient.post<WorkOrder>(`/api/work-orders/${id}/dispatch`, data),
    onSuccess: (_data, { id }) => invalidate(id),
  })
}

export function useRedispatchWorkOrder() {
  const invalidate = useInvalidateWorkOrderQueries()
  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: DispatchRequest }) =>
      apiClient.post<WorkOrder>(`/api/work-orders/${id}/redispatch`, data),
    onSuccess: (_data, { id }) => invalidate(id),
  })
}

export function useBindDeviceAsset() {
  const invalidate = useInvalidateWorkOrderQueries()
  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: BindDeviceAssetRequest }) =>
      apiClient.post<WorkOrder>(`/api/work-orders/${id}/bind-device-asset`, data),
    onSuccess: (_data, { id }) => invalidate(id),
  })
}

export function useStartDiagnosis() {
  const invalidate = useInvalidateWorkOrderQueries()
  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: StartDiagnosisRequest }) =>
      apiClient.post<WorkOrder>(`/api/work-orders/${id}/diagnosis/start`, data),
    onSuccess: (_data, { id }) => invalidate(id),
  })
}

export function useCompleteDiagnosis() {
  const invalidate = useInvalidateWorkOrderQueries()
  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: CompleteDiagnosisRequest }) =>
      apiClient.post<WorkOrder>(`/api/work-orders/${id}/diagnosis/complete`, data),
    onSuccess: (_data, { id }) => invalidate(id),
  })
}

export function usePrepareParts() {
  const invalidate = useInvalidateWorkOrderQueries()
  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: PreparePartsRequest }) =>
      apiClient.post<WorkOrder>(`/api/work-orders/${id}/parts/prepare`, data),
    onSuccess: (_data, { id }) => invalidate(id),
  })
}

export function useCheckParts() {
  const invalidate = useInvalidateWorkOrderQueries()
  return useMutation({
    mutationFn: ({ id, reason }: { id: number; reason: string }) =>
      apiClient.post<WorkOrder>(`/api/work-orders/${id}/parts/check`, { reason } as ReasonRequest),
    onSuccess: (_data, { id }) => invalidate(id),
  })
}

export function useStartRepair() {
  const invalidate = useInvalidateWorkOrderQueries()
  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: RepairStartRequest }) =>
      apiClient.post<WorkOrder>(`/api/work-orders/${id}/repair/start`, data),
    onSuccess: (_data, { id }) => invalidate(id),
  })
}

export function useCompleteRepair() {
  const invalidate = useInvalidateWorkOrderQueries()
  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: RepairCompleteRequest }) =>
      apiClient.post<WorkOrder>(`/api/work-orders/${id}/repair/complete`, data),
    onSuccess: (_data, { id }) => invalidate(id),
  })
}

export function useReportMissingParts() {
  const invalidate = useInvalidateWorkOrderQueries()
  return useMutation({
    mutationFn: ({ id, parts }: { id: number; parts: PartItemRequest[] }) =>
      apiClient.post<WorkOrder>(`/api/work-orders/${id}/repair/report-missing-parts`, { parts }),
    onSuccess: (_data, { id }) => invalidate(id),
  })
}

export function useRequestRediagnosis() {
  const invalidate = useInvalidateWorkOrderQueries()
  return useMutation({
    mutationFn: ({ id, reason }: { id: number; reason: string }) =>
      apiClient.post<WorkOrder>(`/api/work-orders/${id}/repair/request-rediagnosis`, {
        reason,
      } as ReasonRequest),
    onSuccess: (_data, { id }) => invalidate(id),
  })
}

export function useAcceptancePass() {
  const invalidate = useInvalidateWorkOrderQueries()
  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: AcceptanceRequest }) =>
      apiClient.post<WorkOrder>(`/api/work-orders/${id}/acceptance/pass`, data),
    onSuccess: (_data, { id }) => invalidate(id),
  })
}

export function useAcceptanceFail() {
  const invalidate = useInvalidateWorkOrderQueries()
  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: AcceptanceRequest }) =>
      apiClient.post<WorkOrder>(`/api/work-orders/${id}/acceptance/fail`, data),
    onSuccess: (_data, { id }) => invalidate(id),
  })
}
